/**
 * @prettier
 */

import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import {
  CARD_TYPES,
  AVAILABLE_PAPER_OPTIONS_BY_CARD_TYPE,
  AVAILABLE_MAGNET_OPTIONS_BY_CARD_TYPE,
  AVAILABLE_TYPE_OPTIONS_BY_CARD_TYPE,
} from 'cards/constants/CardTypes';
import {
  BASE_PAPER_TYPES,
  PRODUCT_OPTION_DETAILS,
  TYPE_OPTION_DETAILS,
} from 'cards/constants/CardLevelOptions';
import _maxBy from 'lodash/maxBy';
import { matchOptionDetails } from '../../../pages/ProductDetail/utils';
import FeatureFlags from '../../../../util/featureFlags';

// check if the card is the Menu one
export const isMenuCard = (optionType, cardType) =>
  optionType === 'size' && cardType === CARD_TYPES.menu;

// Get a list of the option values that are enabled
export const getEnabledOptionsValuesList = (options, cardType, optionType, isAdminView) => {
  return options.reduce((acc, curr) => {
    if (curr.enabled && curr.value !== 'digital') {
      acc.push(isMenuCard(optionType, cardType, isAdminView) ? `menu-${curr.value}` : curr.value);
    }
    return acc;
  }, []);
};

// Provide visible option list according to card type
export const enrichCardLevelOptions = (
  key,
  availableOptions,
  cardType,
  enabledOptionsValuesList,
  activeMediumKey,
  isAdminView
) => {
  if (key === 'color') {
    return availableOptions;
  }

  function getVisibleOptionList() {
    if (key === 'type') {
      return AVAILABLE_TYPE_OPTIONS_BY_CARD_TYPE[cardType];
    }
    if (activeMediumKey === 'magnet') {
      return AVAILABLE_MAGNET_OPTIONS_BY_CARD_TYPE[cardType][key];
    }
    return isAdminView && AVAILABLE_PAPER_OPTIONS_BY_CARD_TYPE.ADMIN[cardType]
      ? AVAILABLE_PAPER_OPTIONS_BY_CARD_TYPE.ADMIN[cardType][key]
      : AVAILABLE_PAPER_OPTIONS_BY_CARD_TYPE[cardType][key];
  }

  const visibleOptionsList = getVisibleOptionList();
  if (!visibleOptionsList) {
    return [];
  }

  return visibleOptionsList.map(option => {
    const activeDetails =
      key === 'type' ? TYPE_OPTION_DETAILS[option] : PRODUCT_OPTION_DETAILS[option];
    // If not enabled for card-type set option as disabled
    return { ...activeDetails, disabled: !enabledOptionsValuesList.includes(option) };
  });
};

export const getOptionPricing = (pricing, cardType, medium, size, option) => {
  if (pricing && cardType && medium && size && option) {
    const sizePricing = _get(pricing, [cardType, medium, size]);

    if (sizePricing) {
      return option === 'size' ? sizePricing : sizePricing[option];
    }
  }
  return null;
};

export const getMaxOptionPrice = variations =>
  (variations && variations.length && _maxBy(variations, 'price_cents').price_cents) || 0;

export const getOptionPrice = (
  pricing,
  cardType,
  optionType,
  optionValue,
  activeSize,
  activeMediumKey
) => {
  const seachForSize = optionType === 'size' ? optionValue : activeSize;
  const sizePricing = getOptionPricing(
    pricing,
    cardType,
    activeMediumKey,
    seachForSize,
    optionType
  );
  let result;
  if (sizePricing && optionType && optionValue) {
    if (optionType === 'size') {
      result = sizePricing.delta;
    } else {
      result = sizePricing && sizePricing[optionValue];
    }
  }

  return result || 0;
};

export const enrichOptionsAccSelection = (
  key,
  cardTypeOptions,
  activeSelection,
  cardType,
  pricing,
  activeMediumKey
) => {
  return cardTypeOptions.map(option => {
    const { size } = activeSelection;
    const { value } = option;
    const optionPrice = getOptionPrice(pricing, cardType, key, value, size, activeMediumKey);
    if (
      (FeatureFlags.get('cardsDisableMagnets') && key === 'type' && value === 'magnet') ||
      // postcard rounded is always disabled
      (size === 'postcard' && key === 'silhouette' && value === 'rounded') ||
      // postcard never has these paper-types
      (size === 'postcard' &&
        key === 'paper-type' &&
        ['pearlized', 'linen', 'felt', 'double-thick', 'triple-thick'].includes(value)) ||
      // holiday folded does not have round corners
      (cardType === CARD_TYPES.holiday &&
        size === 'folded-petite' &&
        key === 'silhouette' &&
        value === 'rounded') ||
      // Folded petite size does not have these paper-types
      (size === 'folded-petite' &&
        key === 'paper-type' &&
        ['recycled', 'double-thick', 'triple-thick'].includes(value))
    ) {
      return {
        ...option,
        disabled: true,
        price: `+ ${(optionPrice / 100).toFixed(2).toString()} Ea.`,
      };
    }

    return {
      ...option,
      price: `+ ${(optionPrice / 100).toFixed(2).toString()} Ea.`,
    };
  });
};

export const sectionIsHidden = (
  optionType,
  isStampFoilCard,
  cardType,
  enrichedOptions,
  activeSelection,
  isLetterpress
) => {
  return (
    // Stamp foil STD cards do not display options for size or type
    (cardType === CARD_TYPES.std && isStampFoilCard && ['size', 'type'].includes(optionType)) ||
    // Stamp foil HC cards do not display options for type
    (FeatureFlags.get('stampFoilHC') &&
      cardType === CARD_TYPES.holiday &&
      isStampFoilCard &&
      optionType === 'type') ||
    // Stamp foil cards don't display foil-color options
    (isStampFoilCard && optionType === 'foil-color') ||
    // Letterpress cards don't display foil-color options
    (isLetterpress && optionType === 'foil-color') ||
    // Letterpress cards don't display type options
    (isLetterpress && optionType === 'type') ||
    // Postcard size -> no custom foil options
    (optionType === 'foil-color' && activeSelection.size === 'postcard') ||
    // Empty options aren't displayed
    enrichedOptions.length <= 1
  );
};

export const getCurrentOptionDimension = (selection, optionType) => {
  if (!selection) {
    return null;
  }
  const optionMatch = matchOptionDetails(selection.value, optionType);
  const currentDimension = (optionMatch && optionMatch.subTitle) || null;
  return currentDimension;
};

export const getCurrentOptionLabel = ({ selection, optionType, cardType }) => {
  if (!selection) {
    return '';
  }
  const optionMatch = matchOptionDetails(selection.value, optionType);
  const currentLabel = isMenuCard(optionType, cardType)
    ? `${selection.value.replace('x', ' x ')} in.`
    : (optionMatch && optionMatch.label) || selection.label || '';
  return currentLabel;
};

export const isComparisonModuleHidden = (cardType, options) => {
  return cardType === CARD_TYPES.ctd && _isEqual(options, BASE_PAPER_TYPES);
};
